import PreviewAssignment from "../../Components/PreviewAssignment";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { useLazyGetCourseWorkStudentQuery } from "../../services/assignments.api";
import { encapsulateCourseWorkForSubmission } from "../../Utils/encapsulateCourseWork";
import CustomSpin from "../../Components/customSpin/CustomSpin";
import PreviewAssignmentForEssay from "../../Components/PreviewAssignmentForEssay";
import StudentAssignmentCorrectionsPreview from "../../Components/studentAssignmentCorrectionsPreview";
import { Button } from "antd";
import style from "./assignmentDetails.module.scss";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { FormattedMessage } from "react-intl";

const AssignmentDetails = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [isPreview, setIsPreview] = useState(true);
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [showNewAttempt, setShowNewAttempt] = useState(false);

  const [
    triggerGetAssignmentsByStudent,
    { data: dataAssignments, isLoading: isLoadingAssignmentsByStudent },
  ] = useLazyGetCourseWorkStudentQuery();

  useEffect(() => {
    if (assignmentId) {
      triggerGetAssignmentsByStudent(assignmentId);
    }
  }, [assignmentId]);

  const courseWork = dataAssignments?.["hydra:member"]?.[0];
  const submissions = dataAssignments?.["hydra:member"]?.[1] || [];
  const countStudentSubmissions = submissions?.length || 0;
  const submissionsIds = submissions?.map((submission) => submission?.id) || [];

  const hasAttemptsLeft =
    countStudentSubmissions < (courseWork?.setting?.attempts || 0);

  const latestSubmission =
    submissions?.length > 0 ? submissions[submissions.length - 1] : null;

  const currentIndex = useMemo(() => {
    if (showNewAttempt) {
      return submissionsIds.length;
    }

    if (!currentSubmission) {
      return -1;
    }

    return submissionsIds?.findIndex(
      (submissionId) => submissionId === currentSubmission?.id,
    );
  }, [submissionsIds, currentSubmission, showNewAttempt]);

  const hasPreviousSubmission =
    (showNewAttempt && submissions.length > 0) ||
    (submissions.length > 0 && currentIndex > 0);

  const previousSubmission =
    currentIndex > 0 ? submissionsIds[currentIndex - 1] : null;

  const hasNextSubmission = showNewAttempt
    ? false
    : hasAttemptsLeft || currentIndex < submissionsIds.length - 1;

  const formatSubmissionData = (studentQuestionAnswers, llmAnswers = []) => {
    if (!studentQuestionAnswers?.length) return null;

    return studentQuestionAnswers?.map((item) => {
      const { question, ...data } = item;

      const matchingLlmAnswer = llmAnswers?.find(
        (llmItem) => llmItem.question.id === question.id,
      );

      return {
        ...data,
        ...question,
        llmAnswer: matchingLlmAnswer
          ? {
              answer: matchingLlmAnswer.output,
              score: matchingLlmAnswer.score || null,
            }
          : null,
      };
    });
  };

  const handlePrevious = () => {
    if (!hasPreviousSubmission) return;

    if (showNewAttempt) {
      setShowNewAttempt(false);
      setCurrentSubmission(latestSubmission);
    } else if (previousSubmission) {
      const submission = submissions?.find(
        (submission) => submission.id === previousSubmission,
      );
      setCurrentSubmission(submission);
      setShowNewAttempt(false);
    }
  };

  const handleNext = () => {
    if (showNewAttempt) {
      return;
    }

    const nextSubmissionIndex = currentIndex + 1;

    if (nextSubmissionIndex < submissionsIds.length) {
      const nextSubmissionId = submissionsIds[nextSubmissionIndex];
      const submission = submissions?.find(
        (submission) => submission.id === nextSubmissionId,
      );
      setCurrentSubmission(submission);
      setShowNewAttempt(false);
    } else if (hasAttemptsLeft) {
      setCurrentSubmission(null);
      setShowNewAttempt(true);
    }
  };

  useEffect(() => {
    if (
      hasAttemptsLeft &&
      (!submissions?.length ||
        submissions.length < courseWork?.setting?.attempts)
    ) {
      setShowNewAttempt(true);
      setCurrentSubmission(null);
    } else if (latestSubmission) {
      setShowNewAttempt(false);
      setCurrentSubmission(latestSubmission);
    }
  }, [submissions, latestSubmission, hasAttemptsLeft, courseWork]);

  const dataFormated = useMemo(() => {
    if (!dataAssignments || !courseWork) return;

    const { quiz, ...courseWorkData } = courseWork;
    const { questions } = quiz || {};

    if (!showNewAttempt && currentSubmission) {
      const llmAnswers = currentSubmission?.preAssesmentQuiz?.llmAnswerQuizzes;
      const formattedQuestions = formatSubmissionData(
        currentSubmission?.studentQuestionAnswers,
        llmAnswers,
      );

      return {
        questions: formattedQuestions,
        id: courseWork?.id || null,
        isPracticeMode: courseWork?.isPracticeMode || null,
        description: courseWork?.description || null,
        title: courseWork?.title || null,
        type: courseWork?.type || null,
        allowHandWriting: courseWork?.allowHandWriting || null,
        allowUploadFile: courseWork?.allowUploadFile || null,
        setting: courseWork?.setting || [],
        studentQuestionAnswers: currentSubmission?.studentQuestionAnswers || [],
        status: currentSubmission?.status || null,
        dueDate: courseWork?.dueDate || null,
      };
    }

    if (showNewAttempt || !submissions?.length) {
      setIsPreview(false);
      return encapsulateCourseWorkForSubmission({
        ...courseWorkData,
        questions,
      });
    }

    return null;
  }, [dataAssignments, currentSubmission, showNewAttempt]);

  if (isLoadingAssignmentsByStudent) {
    return <CustomSpin />;
  }

  const canAccessCorrection =
    courseWork?.isPracticeMode && courseWork?.setting?.hasAttempts;

  const getAttemptDisplay = () => {
    if (showNewAttempt || submissions.length === 0) {
      return <FormattedMessage id={"assignment.details.newAttempt"} />;
    }

    if (currentIndex >= 0) {
      return (
        <FormattedMessage
          id={"assignment.details.attempts"}
          values={{ attempt: currentIndex + 1 }}
        />
      );
    }

    return <FormattedMessage id={"assignment.details.newAttempt"} />;
  };

  const HeaderComponent = () => {
    return (
      <>
        <div className={style.headerLeft}>
          <button className={style.backButton} onClick={() => navigate(-1)}>
            <LeftOutlined />
          </button>
          <h2 className={style.title}>{dataFormated?.title}</h2>
        </div>
        <div className={style.headerRight}>
          <div className={style.attemptsPagination}>
            <Button
              icon={<LeftOutlined />}
              onClick={() => handlePrevious()}
              disabled={!hasPreviousSubmission}
            />
            <span className={style.attemptsTitle}>
              <label>{getAttemptDisplay()}</label>
            </span>
            <Button
              icon={<RightOutlined />}
              onClick={() => handleNext()}
              disabled={!hasNextSubmission}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div style={{ paddingRight: 20, marginTop: 20 }}>
      {canAccessCorrection && !showNewAttempt && currentSubmission ? (
        <StudentAssignmentCorrectionsPreview
          assignments={dataFormated}
          submissions={submissions}
          isPreview={true}
          leftHeader={canAccessCorrection && <HeaderComponent />}
        />
      ) : dataFormated?.type !== "Essay" ? (
        <PreviewAssignment
          assignments={dataFormated}
          isPreview={isPreview}
          leftHeader={canAccessCorrection && <HeaderComponent />}
          countStudentSubmissions={countStudentSubmissions}
          submissions={submissions}
          showAttemptNavigation={true}
        />
      ) : (
        <PreviewAssignmentForEssay
          assignments={dataFormated}
          isPreview={isPreview}
          description={dataFormated?.description}
          allowBack={true}
          leftHeader={canAccessCorrection && <HeaderComponent />}
          countStudentSubmissions={countStudentSubmissions}
          submissions={submissions}
          showAttemptNavigation={true}
        />
      )}
    </div>
  );
};

export default AssignmentDetails;
